import { PickerValidDate } from '@mui/x-date-pickers';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import moment, { Moment } from 'moment';
import { useCallback, useMemo } from 'react';

import { ICONS_TYPES } from '../Icon';

import { Sizes } from './enums';
import {
  AttentionIcon,
  ErrorMessage,
  ErrorMessageContainer,
  Label,
  RequiredMark,
  Root,
  StyledDateRangePicker,
} from './styles';
import { DateRangeValue, Props } from './types';

const DateRangePicker = ({
  className,
  disabled = false,
  error,
  format = 'YYYY-MM-DD',
  fullWidth = false,
  label,
  loading,
  maxDate,
  minDate,
  name,
  onChange,
  readOnly = false,
  required,
  showLabel,
  size = Sizes.medium,
  value,
}: Props) => {
  const disabledOrLoading = useMemo(
    () => disabled || loading,
    [disabled, loading],
  );
  const isClearable = useMemo(
    () => !readOnly && !disabled,
    [disabled, readOnly],
  );

  const handleChange = useCallback(
    (newDateRange: DateRange<PickerValidDate>) => {
      const [start, end] = newDateRange;
      const formattedRange: DateRangeValue = [
        start ? moment(start as Moment).format(format) : null,
        end ? moment(end as Moment).format(format) : null,
      ];
      onChange?.(formattedRange);
    },
    [format, onChange],
  );

  const momentValue: DateRange<Moment> = useMemo(() => {
    if (!value) return [null, null];
    return [
      value[0] ? moment(value[0]) : null,
      value[1] ? moment(value[1]) : null,
    ];
  }, [value]);

  return (
    <Root className={className}>
      {label && showLabel && (
        <Label htmlFor={name} error={!!error}>
          {required && !disabledOrLoading ? (
            <span>
              {label}
              <RequiredMark error={!!error}>*</RequiredMark>
            </span>
          ) : (
            label
          )}
        </Label>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <StyledDateRangePicker
          disabled={disabledOrLoading}
          format={format}
          fullWidth={fullWidth}
          label={showLabel ? undefined : label}
          maxDate={maxDate ? moment(maxDate) : undefined}
          minDate={minDate ? moment(minDate) : undefined}
          name={name}
          onChange={handleChange}
          readOnly={readOnly}
          slots={{ field: SingleInputDateRangeField }}
          slotProps={{
            field: { clearable: isClearable },
            textField: {
              error: !!error,
              required: required,
            },
            popper: {
              sx: { zIndex: 1210 },
            },
          }}
          sizeType={size}
          value={momentValue}
        />
      </LocalizationProvider>
      {!!error && (
        <ErrorMessageContainer sizetype={size}>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export type { Props, DateRangeValue };
export { Sizes };

export default DateRangePicker;
